import { KeyboardEvent, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";

// Buscador global de tabla (Postulaciones y Formularios). El texto se busca en
// todas las columnas, en el backend.
//
// Para no consultar al backend por cada tecla, la búsqueda se dispara cuando la
// persona deja de escribir (debounce). "Buscar" y Enter la disparan al tiro, y
// la X limpia el texto y vuelve a mostrar todo.
//
// Mientras la búsqueda está en curso (esperando el debounce o esperando la
// respuesta de la tabla, `loading`) la lupa pasa a ser un spinner y aparece
// "Buscando…": así la persona sabe que lo que escribió se está buscando y no
// necesita apretar "Buscar".
//
// `value` es la búsqueda ya aplicada (vive en la URL). El texto del input se
// sincroniza con ella solo cuando cambia desde afuera (p. ej. al navegar con un
// enlace), nunca con el eco de una búsqueda propia: si la respuesta llega
// mientras la persona sigue escribiendo, no se le pisa lo que está tecleando.
//
// Se busca desde 3 caracteres (con menos, la consulta trae casi toda la tabla y
// solo carga el backend) y los puntos se eliminan al escribir/pegar, para que
// un RUT con formato "19.406.080-7" se busque como "19406080-7".

export const TABLE_SEARCH_DEBOUNCE_MS = 450;
export const TABLE_SEARCH_MIN_LENGTH = 3;

/** Quita los puntos (formato de RUT) y los espacios de los extremos. */
export const normalizeTableSearch = (term: string): string => term.replace(/\./g, "").trim();

/** El término alcanza para buscar, o está vacío (limpiar la búsqueda). */
const isSearchableTerm = (term: string): boolean =>
  term.length === 0 || term.length >= TABLE_SEARCH_MIN_LENGTH;

interface TableSearchInputProps {
  value: string;
  onSearch: (term: string) => void;
  /** La tabla está cargando resultados. */
  loading?: boolean;
  placeholder?: string;
  debounceMs?: number;
  /** Alto fijo en px, para igualarlo a los botones del toolbar. */
  height?: number;
}

const TableSearchInput = ({
  value,
  onSearch,
  loading = false,
  height,
  placeholder = "Buscar en la tabla…",
  debounceMs = TABLE_SEARCH_DEBOUNCE_MS,
}: TableSearchInputProps) => {
  const [text, setText] = useState(value);
  const [isDebouncing, setIsDebouncing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const lastEmittedRef = useRef(value.trim());
  const onSearchRef = useRef(onSearch);
  onSearchRef.current = onSearch;
  const valueRef = useRef(value);
  valueRef.current = value;
  const debounceTimerRef = useRef<number | null>(null);

  const cancelPendingSearch = () => {
    if (debounceTimerRef.current !== null) {
      window.clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    setIsDebouncing(false);
  };

  useEffect(() => {
    if (value.trim() === lastEmittedRef.current) return;
    lastEmittedRef.current = value.trim();
    setText(value);
  }, [value]);

  const normalizedText = normalizeTableSearch(text);
  const isTooShort = !isSearchableTerm(normalizedText);

  const emit = (term: string) => {
    cancelPendingSearch();
    const normalized = normalizeTableSearch(term);
    if (!isSearchableTerm(normalized)) return;
    lastEmittedRef.current = normalized;
    if (normalized !== valueRef.current.trim()) onSearchRef.current(normalized);
  };

  useEffect(() => {
    if (normalizedText === lastEmittedRef.current || isTooShort) return;
    setIsDebouncing(true);
    debounceTimerRef.current = window.setTimeout(() => emit(text), debounceMs);
    return cancelPendingSearch;
  }, [text, debounceMs]);

  const searchNow = () => emit(text);

  const clear = () => {
    setText("");
    emit("");
    inputRef.current?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      searchNow();
    } else if (event.key === "Escape" && text) {
      event.stopPropagation();
      clear();
    }
  };

  // Emitida pero la tabla aún no la recibe (la URL todavía no se actualiza).
  const isWaitingForTable = lastEmittedRef.current !== value.trim();
  const isSearching = isDebouncing || isWaitingForTable || (loading && (text.trim() !== "" || value !== ""));
  const showMinLengthHint = isTooShort && !isSearching;
  const hintText = isSearching ? "Buscando…" : showMinLengthHint ? `Mínimo ${TABLE_SEARCH_MIN_LENGTH} caracteres` : "";

  return (
    <div role="search" style={{ display: "flex", alignItems: "stretch", minWidth: 0, flex: "1 1 320px", height: height ? `${height}px` : undefined }}>
      <style>{`
        .table-search-field:focus-within {
          border-color: #0065DD !important;
          box-shadow: 0 0 0 3px rgba(0,101,221,0.1);
        }
        .table-search-input::placeholder { color: #94a3b8; }
        .table-search-clear:hover { background: #f1f5f9 !important; color: #334155 !important; }
      `}</style>
      <div
        className="table-search-field"
        style={{
          position: "relative", display: "flex", alignItems: "center", flex: 1,
          border: "1px solid #e2e8f0", borderRight: "none", borderRadius: "8px 0 0 8px",
          background: "#fff", transition: "border-color 0.15s, box-shadow 0.15s",
        }}
      >
        <span style={{ position: "absolute", left: "10px", display: "flex", alignItems: "center", pointerEvents: "none" }}>
          {isSearching ? (
            <CircularProgress size={15} thickness={5} sx={{ color: "#0065DD" }} aria-hidden="true" />
          ) : (
            <SearchIcon style={{ fontSize: 17, color: "#94a3b8" }} />
          )}
        </span>
        <input
          ref={inputRef}
          type="text"
          className="table-search-input"
          value={text}
          placeholder={placeholder}
          aria-label={placeholder}
          aria-busy={isSearching}
          maxLength={120}
          onChange={(event) => setText(event.target.value.replace(/\./g, ""))}
          onKeyDown={handleKeyDown}
          style={{
            width: "100%", minWidth: 0,
            padding: `8px ${isSearching ? 110 : showMinLengthHint ? 160 : 34}px 8px 34px`,
            border: "none", outline: "none", background: "transparent", borderRadius: "8px 0 0 8px",
            fontFamily: "Roboto, sans-serif", fontSize: "13px", color: "#334155",
          }}
        />
        <span
          aria-live="polite"
          style={{
            position: "absolute", right: text ? "34px" : "12px",
            fontFamily: "Roboto, sans-serif", fontSize: "12px", fontWeight: 500,
            color: showMinLengthHint ? "#b45309" : "#0065DD", pointerEvents: "none", whiteSpace: "nowrap",
          }}
        >
          {hintText}
        </span>
        {text && (
          <button
            type="button"
            className="table-search-clear"
            onClick={clear}
            aria-label="Limpiar búsqueda"
            title="Limpiar búsqueda"
            style={{
              position: "absolute", right: "6px", display: "flex", alignItems: "center", justifyContent: "center",
              width: "22px", height: "22px", padding: 0, borderRadius: "50%", border: "none",
              background: "transparent", color: "#94a3b8", cursor: "pointer", transition: "background 0.15s, color 0.15s",
            }}
          >
            <CloseIcon style={{ fontSize: 15 }} />
          </button>
        )}
      </div>
      <motion.button
        type="button"
        onClick={searchNow}
        disabled={isTooShort}
        title={isTooShort ? `Ingresa al menos ${TABLE_SEARCH_MIN_LENGTH} caracteres` : undefined}
        whileHover={{ scale: isTooShort ? 1 : 1.02 }}
        whileTap={{ scale: isTooShort ? 1 : 0.97 }}
        style={{
          display: "flex", alignItems: "center", gap: "5px",
          padding: "7px 14px", borderRadius: "0 8px 8px 0",
          border: "1px solid #0065DD", background: "#0065DD", color: "#fff",
          fontFamily: "Roboto, sans-serif", fontSize: "13px", fontWeight: 600,
          cursor: isTooShort ? "not-allowed" : "pointer", opacity: isTooShort ? 0.6 : 1,
          whiteSpace: "nowrap",
        }}
      >
        Buscar
      </motion.button>
    </div>
  );
};

export default TableSearchInput;
